<template>
  <section>
    <transition name="fade">
      <section v-show="showWarning" class="warning">
        <div class="content scrollable">
          <div class="header">
            <div class="warning-icon"></div>
            <h2>{{ i18n.t('legal:please_update') }}</h2>
            <p>{{ i18n.t('legal:warning_info') }}</p>
          </div>
          <confirmation-list class="flow-confirmation" :screenParams="screenParams" :onlyEntries="onlyEntries"></confirmation-list>
          <action-bar class="actions">
            <ul>
              <li @click="confirm" class="primary">
                <spinner v-if="isLoading" type="ring" class="custom-spinner" />
                <span v-else>{{ i18n.t('legal:submit') }}</span>
              </li>
              <li @click="closeWarning" class="closing">
                <span>{{ i18n.t('base:close') }}</span>
              </li>
            </ul>
          </action-bar>
        </div>
      </section>
    </transition>
    <transition name="fade">
      <section v-show="!showWarning">
        <div class="content scrollable">
          <div class="header" v-if="summaryDoubleCheck">
            <h2>{{ i18n.t('legal:please_double_check') }}</h2>
            <h6 v-if="isLastTry">{{ i18n.t('legal:last_try') }}</h6>
            <h6 v-else>{{ i18n.t('legal:informations') }}</h6>
          </div>
          <div class="header" v-else>
            <h2 v-if="!isDataChanged">{{ i18n.t('legal:please_confirm') }}</h2>
            <h2 v-else-if="isVerifying">{{ i18n.t('legal:verifying') }}</h2>
            <h2 v-else>{{ i18n.t('legal:updated') }}</h2>
            <h6 v-if="isDataChanged">{{ i18n.t('legal:details_updated') }}</h6>
            <h6 v-else>{{ i18n.t('legal:informations') }}</h6>
          </div>
          <confirmation-list :class="{ 'double-check': isDoubleCheck, 'verifying': isVerifying }" :onlyEntries="onlyEntries"></confirmation-list>
          <helper class="hint" v-if="partner.variant === 'bank-login' || isSchufaEnabled">{{ i18n.t('specifics:legal.schuffa-info') }}</helper>
        </div>
        <action-bar class="actions" :fade="true">
          <ul>
            <li @click="confirm" class="primary">
              <spinner v-if="isLoading" type="ring" class="custom-spinner" />
              <span v-else>{{ isDataChanged ? i18n.t('legal:play_now') : i18n.t('legal:confirm') }}</span>
            </li>
          </ul>
        </action-bar>
      </section>
    </transition>
  </section>
</template>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import '../assets/mixins';

  ul {
    @include button-list;

    li{
      text-align: center;
      padding: 0;
    }
  }

  .actions /deep/ {
    ul li.primary {
      min-height: 60px;
      position: relative;
    }
  }

  .hint {
    margin-bottom: 20px;
    margin-top: -5px;
    text-align: center;
  }

  section {
    margin-top: 0 !important;
    & .warning {
      margin-top: -30px !important;
    }

    & .content {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 100px;
      padding: 80px 30px 30px;
      margin: -30px;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;

      @media (max-width: #{$small-phone-width}) {
        bottom: 60px;
      }

      @include desktop-big {
        padding: 30px 100px 0;
        left: -85px;
        right: -85px;
        bottom: 100px;
        margin-top: -23px;
      }
    }
  }

  .warning {
    .content {
      background-color: #FFF;
      z-index: 10;
      padding-top: 40px;
      bottom: 0;
    }
    .actions {
      position: static;
      padding: 0;
    }
    .header {
      .warning-icon {
        background-image: url('~static/warning.svg');
        width: 87px;
        height: 75px;
        margin: 0 auto;
        margin-bottom: 20px;
      }
      h2 {
        margin-bottom: 15px;
      }
      p {
        color: #4E5053;
        font-size: 16px;
        font-weight: 300;
        line-height: 20px;
      }
    }
    .flow-confirmation /deep/ li{
      border: none !important;
      padding: 7px 30px;
      a {
        color: #6F7B7B;
      }
    }
    .closing {
      box-shadow: none;
      text-decoration: underline;
      font-size: 16px;
      color: #798291;
      font-family: $mainFontFamily;
      background: none;
    }
  }

  .double-check {
    & /deep/ li{
      border: none !important;
      padding: 7px 30px;
    }
  }

  .verifying {
    opacity: .4;
    & /deep/ li{
      a {
        display: none;
      }
    }
  }

  .header {
    text-align: center;
  }
  h2 {
    font-weight: 700;
    font-size: 22px;
    color: #161616;
    margin: 0 auto;
    line-height: 30px;
    text-align: center;
    color: #161616;
    white-space: pre-line;
  }

  h6 {
    color: #798291;
    font-size: 16px;
    line-height: 22px;
    white-space: pre-line;
    font-weight: 300;
  }

  p {
    font-size: 15px;
    margin-bottom: 15px;
    line-height: 23px;
  }

  /deep/ .custom-spinner .ring div{
    border-color: #404247 transparent transparent transparent;
  }
</style>

<script>
  import Vue from 'vue'
  import { mapGetters, mapState } from 'vuex'
  import ActionBar from '../components/ActionBar'
  import FlowConfirmationList from '../components/FlowConfirmationList'
  import Spinner from '../components/Spinner'
  import * as identIdAdressCheck from '../integrations/identityVerification'
  import BankLoginRMT from '../plugins/regily-mt/banklogin/bankloginRMT'

  export default Vue.component('legal-screen', {
    components: {
      'action-bar': ActionBar,
      'confirmation-list': FlowConfirmationList,
      'spinner': Spinner
    },
    props: ['screenParams'],
    data () {
      return {
        isLoading: false,
        showWarning: false,
        isDoubleCheck: false,
        tracking: null,
        isLastTry: false,
        summaryDoubleCheck: false
      }
    },
    computed: {
      ...mapState(['dataStatus', 'user', 'app', 'partner', 'verify']),
      ...mapGetters(['isVerify', 'isSchufaEnabled']),
      isVerifying () {
        return this.isDoubleCheck && this.isLoading
      },
      onlyEntries () {
        if (this.screenParams.entries) {
          return this.screenParams.entries
        }
        return this.app.country.code === 'NL'
          ? ['full-name', 'birthdate', 'masked-birthdate', 'postal-code', 'city', 'street-name', 'house-number', 'question.state']
          : this.showWarning
            ? [
              'birthdate',
              'masked-birthdate',
              'tax-number',
              'tax-number-recovery',
              'first-name-spain-flow',
              'last-name-spain-flow',
              'last-name-second-spain-flow'
            ]
            : []
      },
      isDataChanged () {
        return this.screenParams.dataChanged
      },
      verificationStatus: {
        get () {
          return this.app.verificationStatus
        },
        set (value) {
          this.$store.dispatch('updateApp', {
            verificationStatus: value
          })
        }
      }
    },
    methods: {
      async handleFailedAttempt () {
        const failedAttempts = this.user.bankLoginFailedAttempts || 0
        this.isLoading = false
        this.$store.dispatch('updateSofortIdent', {
          agecheck_result: 'invalid'
        })
        this.tracking.schufa.fail()
        await this.$store.dispatch('updateUser', {
          bankLoginFailedAttempts: failedAttempts + 1
        })
        if (this.user.bankLoginFailedAttempts > 1) {
          this.$store.dispatch('updateApp', {
            idVerificationFailed: true,
            showVerificationRecovery: false
          })
          this.tracking.schufa.error('multiple_failed_attempts')
        } else {
          this.$store.dispatch('updateApp', {
            showVerificationRecovery: true
          })
        }

        this.$store.dispatch('next', this.screenParams.nextScreenRoute)
      },
      triggerError (e) {
        this.$store.dispatch('updateSofortIdent', {
          agecheck_result: 'invalid'
        })
        e && console.warn(e.message)
        this.$store.dispatch('replace', {
          path: '/error/bankIdentInvalid'
        })
      },
      async handleInvalidResults () {
        const schufaMethod = this.isSchufaEnabled && this.verify.methods.schufa
        const recoveryModeConfig = schufaMethod && schufaMethod.recovery
        const recoveryModeEnabled = recoveryModeConfig && recoveryModeConfig.enabled
        const maxFailedAttempts = (recoveryModeConfig && recoveryModeConfig.maxAttempts) || 3
        const failedAttempts = (this.user.schufaFailedAttempts || 0) + 1

        this.verificationResult = 'invalid'
  
        await this.$store.dispatch('updateUser', {
          schufaFailedAttempts: failedAttempts
        })

        await this.$store.dispatch('updateSofortIdent', {
          agecheck_result: 'invalid'
        })

        this.tracking.schufa.fail()

        // Max failed attempts reached
        if (failedAttempts >= maxFailedAttempts) {
          this.$store.dispatch('updateApp', {
            idVerificationFailed: true,
            showVerificationRecovery: false
          })

          this.tracking.schufa.error('multiple_failed_attempts')

          // go to error screen (close)
          this.$store.dispatch('replace', {
            path: '/error/verifyMaxAttempts'
          })

          return
        }

        if (!recoveryModeEnabled) {
          // go to error screen (try again close)
          this.$store.commit('updateRoutingInfo', {
            verifyOriginPath: this.$route.fullPath
          })
          this.$store.dispatch('replace', {
            path: '/error/verifyFailed'
          })
          return
        }

        // go to recovery flow
        this.$store.dispatch('updateApp', {
          showVerificationRecovery: true
        })
        this.$store.dispatch('next', this.screenParams.nextScreenRoute)
      },
      async schufaCheck () {
        this.verificationStatus = 'loading'

        const params = {
          publicKey: this.partner.publicKey,
          skip: this.partner.skipIdentityCheck
        }
  
        const identIdAdressCheckData = await identIdAdressCheck.requestAuthCheck(params, {
          firstName: this.user.firstName,
          lastName: this.user.lastName,
          gender: this.user.gender,
          birthdate: this.user.birthdate,
          postalCode: this.user.postalCode,
          city: this.user.city,
          streetName: this.user.streetName,
          houseNumber: this.user.houseNumber,
          country: 'DE'
        })

        this.tracking.schufa.send()
  
        this.$store.dispatch('updateIdentIdAdressCheck', {
          ...identIdAdressCheckData
        })

        if (!this.partner.skipIdentityCheck) {
          if (identIdAdressCheckData.result === 'invalid') {
            this.verificationStatus = 'failed'
            if (this.isVerify) {
              await this.handleInvalidResults()
              return
            } else {
              this.handleFailedAttempt()
              return
            }
          } else if (identIdAdressCheckData.result === 'error') {
            this.verificationStatus = 'failed'
            this.triggerError()
            this.tracking.schufa.error('failed')
            return
          }
        }

        this.verificationStatus = 'success'
        this.tracking.schufa.success()
  
        this.$store.dispatch('updateApp', { showVerificationRecovery: false })
        this.$store.dispatch('next', this.screenParams.nextScreenRoute)
      },
      async confirm () {
        const shouldShuffaCheck = !this.isVerify || (this.isSchufaEnabled && this.isVerify)
        if (shouldShuffaCheck) {
          await this.schufaCheck()
          return
        } else if (this.app.country.code === 'NL') {
          this.isLoading = true
          await this.$store.dispatch('getIdinStatus', this.screenParams.nextScreenRoute)
          return
        }

        // handle redirect methods
        const { selected } = this.verify
        if (selected.redirectLink) {
          this.$postmate.emit('customRedirect', selected.redirectLink)
          return
        }

        this.$store.dispatch('next', this.screenParams.nextScreenRoute)
      },
      errorPresent (errorSearchName) {
        const errors = this.dataStatus.errors && this.dataStatus.errors.length > 0 ? this.dataStatus.errors : false
        if (errors) {
          return errors.find((error) => {
            return error.name === errorSearchName
          })
        }
      },
      closeWarning () {
        if (this.$postmate) {
          const flowComplete = this.$store.state.app.complete
          this.$postmate.emit('close', flowComplete)
          this.$ma.trackEvent({
            category: 'close-confirmation',
            action: 'exited'
          })
        }
        this.$store.dispatch('reset')
        this.$store.dispatch('next', '/')
      }
    },
    created () {
      const isIdinFlow = this.app.country.code === 'NL'
      const comingFromEditScreen = this.$route.fullPath === this.$store.state.routingInfo.flowConfirmationScreenPath

      this.showWarning = isIdinFlow && !this.$store.state.idinStatus.compareSuccessCheck && !comingFromEditScreen
      this.isDoubleCheck = !!comingFromEditScreen
    },
    mounted () {
      this.$emit('tooltip', ['enter'])
      this.tracking = new BankLoginRMT(this.$rmt)

      const bankLoginFailedAttempts = this.user.bankLoginFailedAttempts
      const schufaFailedAttempts = this.user.schufaFailedAttempts
      this.isLastTry = (bankLoginFailedAttempts && bankLoginFailedAttempts >= 2) || (schufaFailedAttempts && schufaFailedAttempts >= 2)
      this.summaryDoubleCheck = bankLoginFailedAttempts > 0 || schufaFailedAttempts > 0
    },
    beforeRouteLeave (to, from, next) {
      this.verificationStatus = null
      next()
    }
  })
</script>



// WEBPACK FOOTER //
// src/screens/Legal.vue